import {
    AfterViewChecked,
    AfterViewInit,
    Component,
    Inject, OnDestroy,
    OnInit
} from '@angular/core';
import {
    ControlService,
    IconService, ParametersService,
    ScheduleService,
    SubtitlesService
} from "../../../core";
import * as _ from 'lodash';
import {MenuElement, MenuElementType100, MenuType} from "../../../models";
import {ChangeContext, Options} from "@angular-slider/ngx-slider";
import {DOCUMENT} from "@angular/common";
import {Subscription} from "rxjs";

@Component({
    selector: 'schedule-multi',
    templateUrl: './schedule-multi.component.html',
    styleUrls: ['./schedule-multi.component.scss']
})
export class ScheduleMultiComponent implements AfterViewInit, OnInit, AfterViewChecked, OnDestroy {
    startValue: number[] = [];
    private prevStartValue: number[] = [];
    private prevEndValue: number[] = [];
    endValue: number[] = [];
    options: Options[] = [];
    tempVal: string | number = '';
    hasAdjacentValues: boolean = false;
    hasNonAdjacentValues: boolean = false;
    changingSliders = new Set<string>();
    multiTableSubscription: Subscription;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private icon: IconService,
        public control: ControlService,
        private parameters: ParametersService,
        public schedule: ScheduleService,
        private subtitles: SubtitlesService,
    ) {
        this.multiTableSubscription = this.schedule.multiTableChanged$.subscribe(() => {
            this.setMultiTable(true);
        });
    }

    ngOnInit() {
        this.setMultiTable();
    }

    ngAfterViewInit() {
        this.control.setOverflow();
    }

    ngAfterViewChecked() {
        const min = this.document.querySelectorAll('.ngx-slider-pointer-min');
        const max = this.document.querySelectorAll('.ngx-slider-pointer-max');
        let color = '#F50F3F';

        if (this.parameters.selectedModuleCompany === 'wanas') {
            color = '#7AB51D';
        }
        if (this.parameters.selectedModuleCompany === 'klimor') {
            color = '#005bbb';
        }

        const elementVal = this.control.elementVal.getValue() as MenuElementType100;
        min.forEach((el: Element, idx: number) => {
            if (idx === 0 && elementVal.params.type === 0) {
                el.classList.add('ngx-slider-pointer-min--first');
            }
            (el as HTMLSpanElement).style['border-left-color'] = color;
        });
        max.forEach((el: Element) => {
            (el as HTMLSpanElement).style['border-right-color'] = color;
        });
    }

    ngOnDestroy() {
        this.multiTableSubscription.unsubscribe();
    }

    subtitle(val: number): string {
        return val !== 0 ? this.subtitles.setSubtitle(val) : '';
    }

    setIcon(val: number | MenuType): string {
        if (val === null) return;

        return this.icon.setIcon(val);
    }

    changeAdjacentValues(changeContext: ChangeContext, idx: number) {
        if (this.changingSliders.size !== 0) return;

        // first
        if (idx === 0) {
            this.startValue[idx + 1] = changeContext.value;
            this.changingSliders.add(`s${idx+1}`);
        }

        // middle
        if (idx !== 0 && idx <= this.schedule.multiTable.length - 2) {
            // left
            if (idx === 1) {
                if (this.startValue[0] !== changeContext.value) {
                    this.startValue[0] = changeContext.value;
                    this.changingSliders.add(`s0`);
                }
            }
            if (idx > 1) {
                if (this.endValue[idx -1] !== changeContext.value) {
                    this.endValue[idx -1] = changeContext.value;
                    this.changingSliders.add(`e${idx-1}`);
                }
            }
            // right
            if (this.startValue[idx + 1] !== changeContext.highValue) {
                this.startValue[idx + 1] = changeContext.highValue;
                this.changingSliders.add(`s${idx + 1}`);
            }
        }

        // last
        if (idx === this.schedule.multiTable.length - 1) {
            this.endValue[idx - 1] = changeContext.value;
            this.changingSliders.add(`e${idx-1}`);
        }
    }

    onValueChange(modelValue: number, idx: number) {
        if (this.changingSliders.has(`s${idx}`)) {
            this.changingSliders.delete(`s${idx}`);
        }
        if (this.changingSliders.has(`e${idx}`)) {
            this.changingSliders.delete(`e${idx}`);
        }
    }

    changeNonAdjacentValues(changeContext: ChangeContext, idx: number) {
        if (this.changingSliders.size !== 0) return;

        const deltaStart = changeContext.value - this.prevStartValue[idx];
        const deltaEnd = changeContext.highValue - this.prevEndValue[idx];
        const startValue = _.cloneDeep(this.startValue);
        const endValue = _.cloneDeep(this.endValue);

        if (deltaStart !== 0) {
            this.changeStartValue(idx, changeContext.value, startValue, endValue);
        }
        if (deltaEnd !== 0) {
            this.changeEndValue(idx, changeContext.highValue, startValue, endValue);
        }

        this.startValue = startValue;
        this.endValue = endValue;
    }

    private changeStartValue(idx: number, value: number, startValue: number[], endValue: number[]): void {
        if (idx - 1 >= 0 && value <= this.options[idx].maxLimit - 15) {
            if (endValue[idx - 1] > value) {
                if (value > this.options[idx-1].minLimit + 5) {
                    endValue[idx - 1] = value;
                    this.changingSliders.add(`e${idx-1}`);

                    if (startValue[idx - 1] >= value - 5) {
                        startValue[idx - 1] = value - 5;
                        this.changingSliders.add(`s${idx-1}`);

                         if (idx - 2 >= 0) {
                             const nextIdx = idx - 1;
                             this.changeStartValue(nextIdx, value - 5, startValue, endValue);
                         }
                     }
                }
            }
        }
    }

    private changeEndValue(idx: number, highValue: number, startValue: number[], endValue: number[]): void {
        if (idx < this.schedule.multiTable.length - 1) {
            if (startValue[idx + 1] < highValue) {
                if (highValue <= this.options[idx+1].maxLimit + 5
                    && highValue >= this.options[idx].minLimit + 5) {
                    startValue[idx + 1] = highValue;
                    this.changingSliders.add(`s${idx+1}`);

                    if (endValue[idx + 1] <= highValue + 5) {
                        endValue[idx + 1] = highValue + 5;
                        this.changingSliders.add(`e${idx+1}`);

                        if (idx + 1 <= this.schedule.multiTable.length - 1) {
                            const nextIdx = idx + 1;
                            this.changeEndValue(nextIdx, highValue + 5, startValue, endValue);
                        }
                    }
                }
            }
        }
    }

    changeValuesEnd(changeContext: ChangeContext, idx: number) {
        const deltaStart = changeContext.value - this.prevStartValue[idx];
        const deltaEnd = changeContext.highValue - this.prevEndValue[idx];

        // first
        if (idx === 0) {
            this.prevStartValue[0] = changeContext.value;
            this.prevStartValue[1] = changeContext.value;
            this.schedule.swipeRange(deltaStart, idx, 'end');
        }

        // middle
        if (idx !== 0 && idx !== this.schedule.multiTable.length - 1) {
            if (deltaStart !== 0) {
                this.prevStartValue[idx] = changeContext.value;
                this.schedule.swipeRange(deltaStart, idx, 'start');
            }
            if (deltaEnd !== 0) {
                this.prevEndValue[idx] = changeContext.highValue;
                this.schedule.swipeRange(deltaEnd, idx, 'end');
            }
        }

        // last
        if (idx === this.schedule.multiTable.length - 1) {
            this.prevStartValue[idx] = changeContext.value;
            this.prevStartValue[idx - 1] = changeContext.value;
            this.schedule.swipeRange(deltaStart, idx, 'start');
        }
        this.schedule.changeRange();
        this.setMultiTable(true);
    }

    changeNonAdjacentValuesEnd(changeContext: ChangeContext, idx: number) {
        if (this.changingSliders.size !== 0) return;

        const deltaStart = changeContext.value - this.prevStartValue[idx];
        const deltaEnd = changeContext.highValue - this.prevEndValue[idx];

        if (deltaStart !== 0) {
            this.prevStartValue[idx] = changeContext.value;
            this.schedule.rangeNonAdjacentInterval(deltaStart, idx, 'start');
        }
        if (deltaEnd !== 0) {
            this.prevEndValue[idx] = changeContext.highValue;
            this.schedule.rangeNonAdjacentInterval(deltaEnd, idx, 'end');
        }

        this.schedule.changeRange();
    }

    private setOptionsLimits() {
        const options: Options[] = _.cloneDeep(this.options);

        options.forEach((option, idx) => {
            if (idx === 0) {
                option.minLimit = 5;
                option.maxLimit = this.endValue[idx + 1] - 5;
            }
            if (idx !== 0 && idx !== this.schedule.multiTable.length - 1) {
                option.minLimit = this.startValue[idx - 1] + 5;
                option.maxLimit = this.endValue[idx + 1] ? this.endValue[idx + 1] - 5 : 470;
            }
            if (idx === this.schedule.multiTable.length - 1) {
                option.minLimit = this.endValue[idx - 1] + 5;
                option.maxLimit = 475;
            }
        })

        this.options = options;
    }

    setMultiTable(changed: boolean = false) {
        if (!changed) {
            this.schedule.getMultiTable();
        }
        const elementVal = this.control.elementVal.getValue() as MenuElementType100;
        this.hasAdjacentValues = elementVal.params.type === 0;
        this.hasNonAdjacentValues = elementVal.params.type === 1;

        if (this.hasAdjacentValues) {
            this.setAdjacentValues();
        }
        if (this.hasNonAdjacentValues) {
            this.setNonAdjacentValues();
        }

        this.setOptions();

        if (this.hasAdjacentValues) {
            this.setOptionsLimits();
        }

        if (this.hasNonAdjacentValues) {
            this.setNonAdjacentOptionsLimits();
        }

        this.changingSliders.clear();
    }

    sortById(a: MenuElement, b: MenuElement): number {
        return a.id - b.id;
    }

    private setAdjacentValues(): void {
        this.schedule.multiTable.forEach((val, idx) => {
            if (idx === 0) {
                this.startValue[idx] = val.endTime / 3;
                this.prevStartValue[idx] = val.endTime / 3;
            }
            if (idx !== 0 && idx !== this.schedule.multiTable.length - 1) {
                this.startValue[idx] = val.startTime / 3;
                this.prevStartValue[idx] = val.startTime / 3;
                this.endValue[idx] = val.endTime / 3;
                this.prevEndValue[idx] = val.endTime / 3;
            }
            if (idx === this.schedule.multiTable.length - 1) {
                this.startValue[idx] = val.startTime / 3;
                this.prevStartValue[idx] = val.startTime / 3;
            }
        });
    }

    private setNonAdjacentValues(): void {
        this.schedule.multiTable.forEach((val, idx) => {
            this.startValue[idx] = val.startTime / 3;
            this.prevStartValue[idx] = val.startTime / 3;
            this.endValue[idx] = val.endTime / 3;
            this.prevEndValue[idx] = val.endTime / 3;
        });
    }

    private setOptions(): void {
        this.options = this.schedule.multiTable.map((val, idx) => {
            const options: Options = {
                floor: 0,
                ceil: 480,
                step: 1,
                showTicks: false,
                showTicksValues: false,
                minRange: 5,
                noSwitching: true,
                animate: false,
                animateOnMove: false,
            };

            if (this.hasAdjacentValues) {
                if (idx === 0) {
                    options.showSelectionBar = true;
                }
                if (idx === this.schedule.multiTable.length - 1) {
                    options.showSelectionBarEnd = true;
                }
            }

            return options;
        })
    }

    private setNonAdjacentOptionsLimits() {
        const options: Options[] = _.cloneDeep(this.options);

        this.schedule.multiTable.map((val, idx) => {
            options[idx].minLimit = 5 * idx;
            options[idx].maxLimit = 480 - (5 * (this.schedule.multiTable.length - 1 - idx));
        });

        this.options = options;
    }
}
